import $http from '../../shared/http';
import { dateTimeNormaliserLong } from '../../shared/utils';
import {
  parseDateValue,
  parseStringToArray,
} from '../../views/onboarding-data-migration/stage-two/ConvertExcelToJSON';

const defaultMigrationProgress = {
  migrationId: null,
  rowNumber: null,
  startedAt: null,
  endedAt: null,
  status: null,
  type: null,
};

export const onboardings = {
  namespaced: true,
  state: () => {
    return {
      onboardingState: null,
      onboardingData: null,
      isPositionsCreated: null,
      isMembershipMannersCreated: null,
      isZonesCreated: null,
      isMinistriesCreated: null,
      isServiceTypesCreated: null,
      isGivingTypesCreated: null,
      isHeadCountTypesCreated: null,
      isAbsenceReasonsCreated: null,
      isServiceActivitiesCreated: null,
      migrationTemplatesSettings: null,
      dateFormatForMigration: 'YYYY-MM-DD',
      dataMigrationProgress: {
        migrationId: null,
        rowNumber: null,
        startedAt: null,
        endedAt: null,
        status: null,
        type: null,
      },
      onboardingStepOneSteps: {
        task1: {
          title: '$t__onboarding_data_migration_module.create_positions',
        },
        task2: {
          title: '$t__onboarding_data_migration_module.create_membership_manners',
        },
        task3: {
          title: '$t__onboarding_data_migration_module.create_zones',
        },
        task4: {
          title: '$t__onboarding_data_migration_module.create_ministries',
        },
        task5: {
          title: '$t__onboarding_data_migration_module.create_service_types',
        },
        task6: {
          title: '$t__onboarding_data_migration_module.create_giving_types',
        },
        task8: {
          title: '$t__onboarding_data_migration_module.create_service_activities',
        },
        task9: {
          title: '$t__onboarding_data_migration_module.onboarding_summary',
        },
      },
    };
  },
  getters: {
    getOnboardingState: (state) => state.onboardingState,
    getMigrationTemplatesSettings: (state) => state.migrationTemplatesSettings,
    getIsPositionsCreated: (state) => state.isPositionsCreated,
    getIsMembershipMannersCreated: (state) => state.isMembershipMannersCreated,
    getIsZonesCreated: (state) => state.isZonesCreated,
    getIsMinistriesCreated: (state) => state.isMinistriesCreated,
    getIsServiceTypesCreated: (state) => state.isServiceTypesCreated,
    getIsGivingTypesCreated: (state) => state.isGivingTypesCreated,
    getIsHeadCountTypesCreated: (state) => state.isHeadCountTypesCreated,
    getIsAbsenceReasonsCreated: (state) => state.isAbsenceReasonsCreated,
    getIsServiceActivitiesCreated: (state) => state.isServiceActivitiesCreated,
    getOnboardingData: (state) => state.onboardingData,
    /**
     *
     */
    getFilteredOnboardingState: (state, getters) => (stage, step) =>
      getters?.getOnboardingState?.[stage]?.[step] ?? null,
    getOnboardingSummary: (state, getters, rootState, rootGetters) => {
      const churchResourcesExistence = rootGetters['organisations/getChurchResourcesExistence'];

      return [
        {
          name: state.onboardingStepOneSteps.task1.title,
          task_index: 1,
          optional: false,
          is_task_completed: getters.getFilteredOnboardingState('onboarding', 'create_positions')
            ? getters.getFilteredOnboardingState('onboarding', 'create_positions').completed
            : null,
          task_completed_at: getters.getFilteredOnboardingState('onboarding', 'create_positions')
            ? getters.getFilteredOnboardingState('onboarding', 'create_positions').completed_at
            : '',
          is_data_available: !!churchResourcesExistence.positions,
          data_link: {
            name: 'all_positions',
            params: { churchId: rootGetters['auth/inStoreResolvedOrgId']() },
          },
        },
        {
          name: state.onboardingStepOneSteps.task2.title,
          task_index: 2,
          optional: false,
          is_task_completed: getters.getFilteredOnboardingState(
            'onboarding',
            'create_membership_manners'
          )
            ? getters.getFilteredOnboardingState('onboarding', 'create_membership_manners')
                .completed
            : null,
          task_completed_at: getters.getFilteredOnboardingState(
            'onboarding',
            'create_membership_manners'
          )
            ? getters.getFilteredOnboardingState('onboarding', 'create_membership_manners')
                .completed_at
            : '',
          is_data_available: !!churchResourcesExistence.membershipManners,
          data_link: {
            name: 'all_membership_manners',
            params: { churchId: rootGetters['auth/inStoreResolvedOrgId']() },
          },
        },
        {
          name: state.onboardingStepOneSteps.task3.title,
          task_index: 3,
          optional: true,
          is_task_completed: getters.getFilteredOnboardingState('onboarding', 'create_zones')
            ? getters.getFilteredOnboardingState('onboarding', 'create_zones').completed
            : null,
          task_completed_at: getters.getFilteredOnboardingState('onboarding', 'create_zones')
            ? getters.getFilteredOnboardingState('onboarding', 'create_zones').completed_at
            : '',
          is_data_available: !!churchResourcesExistence.zones,
          data_link: {
            name: 'all_zones',
            params: { churchId: rootGetters['auth/inStoreResolvedOrgId']() },
          },
        },
        {
          name: state.onboardingStepOneSteps.task4.title,
          task_index: 4,
          optional: true,
          is_task_completed: getters.getFilteredOnboardingState('onboarding', 'create_ministries')
            ? getters.getFilteredOnboardingState('onboarding', 'create_ministries').completed
            : null,
          task_completed_at: getters.getFilteredOnboardingState('onboarding', 'create_ministries')
            ? getters.getFilteredOnboardingState('onboarding', 'create_ministries').completed_at
            : '',
          is_data_available: !!churchResourcesExistence.ministries,
          data_link: {
            name: 'all_ministries',
            params: { churchId: rootGetters['auth/inStoreResolvedOrgId']() },
          },
        },
        {
          name: state.onboardingStepOneSteps.task5.title,
          task_index: 5,
          optional: false,
          is_task_completed: getters.getFilteredOnboardingState(
            'onboarding',
            'create_service_types'
          )
            ? getters.getFilteredOnboardingState('onboarding', 'create_service_types').completed
            : null,
          task_completed_at: getters.getFilteredOnboardingState(
            'onboarding',
            'create_service_types'
          )
            ? getters.getFilteredOnboardingState('onboarding', 'create_service_types').completed_at
            : '',
          is_data_available: !!churchResourcesExistence.serviceTypes,
          data_link: {
            name: 'all_service_types',
            params: { churchId: rootGetters['auth/inStoreResolvedOrgId']() },
          },
        },
        {
          name: state.onboardingStepOneSteps.task6.title,
          task_index: 6,
          optional: true,
          is_task_completed: getters.getFilteredOnboardingState('onboarding', 'create_giving_types')
            ? getters.getFilteredOnboardingState('onboarding', 'create_giving_types').completed
            : null,
          task_completed_at: getters.getFilteredOnboardingState('onboarding', 'create_giving_types')
            ? getters.getFilteredOnboardingState('onboarding', 'create_giving_types').completed_at
            : '',
          is_data_available: !!churchResourcesExistence.givingTypes,
          data_link: {
            name: 'all_giving_types',
            params: { churchId: rootGetters['auth/inStoreResolvedOrgId']() },
          },
        },
        {
          name: state.onboardingStepOneSteps.task8.title,
          task_index: 8,
          optional: true,
          is_task_completed: getters.getFilteredOnboardingState(
            'onboarding',
            'create_service_activities'
          )
            ? getters.getFilteredOnboardingState('onboarding', 'create_service_activities')
                .completed
            : null,
          task_completed_at: getters.getFilteredOnboardingState(
            'onboarding',
            'create_service_activities'
          )
            ? getters.getFilteredOnboardingState('onboarding', 'create_service_activities')
                .completed_at
            : '',
          is_data_available: !!churchResourcesExistence.serviceActivities,
          data_link: {
            name: 'all_service_activities',
            params: { churchId: rootGetters['auth/inStoreResolvedOrgId']() },
          },
        },
      ];
    },
    isOnboardingNotCompleted: (state, getters) => {
      if (getters.onboardingStatus) {
        const statuses = Object.values(getters.onboardingStatus);
        return statuses.some((status) => !status.status);
      } else return true;
    },
    onboardingStatus: (state, getters) => {
      if (getters.getOnboardingSummary) {
        const statuses = {};
        getters.getOnboardingSummary.forEach((task) => {
          statuses[`${task.name}`] = { status: task.is_data_available || task.optional };
        });
        return statuses;
      } else return null;
    },
    getMembersMigrationSchema: () => {
      return {
        membership_id: { prop: 'membership_id', type: Number, required: true },
        first_name: { prop: 'first_name', type: String, required: true },
        middle_name: { prop: 'middle_name', type: String },
        last_name: { prop: 'last_name', type: String, required: true },
        zone: { prop: 'zone', type: String },
        ministries: {
          prop: 'ministries',
          parse(value) {
            try {
              return parseStringToArray(value);
            } catch (error) {
              throw new Error(error);
            }
          },
        },
        position: {
          prop: 'position',
          type: String,
        },
        status: {
          prop: 'status',
          type: String,
        },
        membership_manner: { prop: 'membership_manner', type: String },
        gender: { prop: 'gender', type: String, required: true },
        is_a_child: {
          prop: 'is_a_child',
          type: String,
          required: true,
        },
        home_phone: { prop: 'home_phone', type: String },
        office_phone: { prop: 'office_phone', type: String },
        email: { prop: 'email', type: String },
        email_2: { prop: 'email_2', type: String },
        date_of_birth: {
          prop: 'date_of_birth',
          /* type: Date, */ parse(value) {
            try {
              return parseDateValue(value);
            } catch (error) {
              throw new Error(error);
            }
          },
        },
        membership_date: {
          prop: 'membership_date',
          /* type: Date, */ parse(value) {
            try {
              return parseDateValue(value);
            } catch (error) {
              throw new Error(error);
            }
          },
        },
        nationality: { prop: 'nationality', type: String },
        state_of_origin: { prop: 'state_of_origin', type: String },
        lga_of_origin: { prop: 'lga_of_origin', type: String },
        current_address: { prop: 'current_address', type: String },
        current_town: { prop: 'current_town', type: String },
        current_lga: { prop: 'current_lga', type: String },
        current_state: { prop: 'current_state', type: String },
        current_country: { prop: 'current_country', type: String },
        mobile_number: { prop: 'mobile_number', type: String },
        mobile_number_2: { prop: 'mobile_number_2', type: String },
        permanent_address: { prop: 'permanent_address', type: String },
        permanent_town: { prop: 'permanent_town', type: String },
        permanent_lga: { prop: 'permanent_lga', type: String },
        permanent_country: { prop: 'permanent_country', type: String },
        permanent_state: { prop: 'permanent_state', type: String },
        home_congregation_address: { prop: 'home_congregation_address', type: String },
        home_congregation_town: { prop: 'home_congregation_town', type: String },
        home_congregation_lga: { prop: 'home_congregation_lga', type: String },
        home_congregation_country: { prop: 'home_congregation_country', type: String },
        home_congregation_state: { prop: 'home_congregation_state', type: String },
        former_place_of_worship_address: { prop: 'former_place_of_worship_address', type: String },
        former_place_of_worship_town: { prop: 'former_place_of_worship_town', type: String },
        former_place_of_worship_lga: { prop: 'former_place_of_worship_lga', type: String },
        former_place_of_worship_country: { prop: 'former_place_of_worship_country', type: String },
        former_place_of_worship_state: { prop: 'former_place_of_worship_state', type: String },
        is_baptised_in_this_congregation: {
          prop: 'is_baptised_in_this_congregation',
          type: String,
        },
        date_of_baptism: {
          prop: 'date_of_baptism',
          parse(value) {
            try {
              return parseDateValue(value);
            } catch (error) {
              throw new Error(error);
            }
          },
        },
        name_of_baptist: { prop: 'name_of_baptist', type: String },
        baptismal_address: { prop: 'baptismal_address', type: String },
        baptismal_town: { prop: 'baptismal_town', type: String },
        baptismal_lga: { prop: 'baptismal_lga', type: String },
        baptismal_country: { prop: 'baptismal_country', type: String },
        baptismal_state: { prop: 'baptismal_state', type: String },
        marital_status: { prop: 'marital_status', type: String },
        is_spouse_a_member: {
          prop: 'is_spouse_a_member',
          type: String,
        },
        date_of_marriage: {
          prop: 'date_of_marriage',
          parse(value) {
            try {
              return parseDateValue(value);
            } catch (error) {
              throw new Error(error);
            }
          },
        },
        spouse_first_name: { prop: 'spouse_first_name', type: String },
        spouse_last_name: { prop: 'spouse_last_name', type: String },
        spouse_phone_number: { prop: 'spouse_phone_number', type: String },
        spouse_email_address: { prop: 'spouse_email_address', type: String },
        no_of_children: { prop: 'no_of_children', type: Number },
        spouse_address: { prop: 'spouse_address', type: String },
        spouse_address_town: { prop: 'spouse_address_town', type: String },
        spouse_address_lga: { prop: 'spouse_address_lga', type: String },
        spouse_address_country: { prop: 'spouse_address_country', type: String },
        spouse_address_state: { prop: 'spouse_address_state', type: String },
        spouse_place_of_worship: { prop: 'spouse_place_of_worship', type: String },
        is_next_of_kin_a_member: {
          prop: 'is_next_of_kin_a_member',
          type: String,
        },
        next_of_kin_gender: { prop: 'next_of_kin_gender', type: String },
        next_of_kin_first_name: { prop: 'next_of_kin_first_name', type: String },
        next_of_kin_middle_name: { prop: 'next_of_kin_middle_name', type: String },
        next_of_kin_last_name: { prop: 'next_of_kin_last_name', type: String },
        next_of_kin_phone_number: { prop: 'next_of_kin_phone_number', type: String },
        next_of_kin_email_address: { prop: 'next_of_kin_email_address', type: String },
        next_of_kin_address: { prop: 'next_of_kin_address', type: String },
        next_of_kin_address_town: { prop: 'next_of_kin_address_town', type: String },
        next_of_kin_address_lga: { prop: 'next_of_kin_address_lga', type: String },
        next_of_kin_address_country: { prop: 'next_of_kin_address_country', type: String },
        next_of_kin_address_state: { prop: 'next_of_kin_address_state', type: String },
        primary_occupation: { prop: 'primary_occupation', type: String },
        secondary_occupation: { prop: 'secondary_occupation', type: String },
        area_of_specialisation: { prop: 'area_of_specialisation', type: String },
        work_address: { prop: 'work_address', type: String },
        work_address_lga: { prop: 'work_address_lga', type: String },
        work_address_country: { prop: 'work_address_country', type: String },
        work_address_state: { prop: 'work_address_state', type: String },
      };
    },
    getDateFormatForMigration: (state) => state.dateFormatForMigration,
    getDataMigrationProgress: (state) => {
      const { migrationId, status, rowNumber, startedAt, endedAt, type } =
        state.dataMigrationProgress;
      const migrationStatus = status === null ? 'notStarted' : status;
      return [
        {
          migration_id: migrationId,
          status: migrationStatus,
          start_time: dateTimeNormaliserLong(startedAt),
          end_time: dateTimeNormaliserLong(endedAt),
          rows_count: rowNumber,
          type,
        },
      ];
    },
  },
  mutations: {
    /**
     * Sets onboarding data from the server.
     */
    setOnboardingState: (state, payload) => (state.onboardingState = payload),
    /**
     * Sets onboarding data from forms for the different stages and steps
     * @param {object} state Store module's state.
     * @param {object} payload Data from the component
     * @param {string} payload.stage The current stage. Onboarding or Data Migration
     * @param {string} payload.step The current step in the current stage
     * @param {object} payload.data The onboard data from the components
     */
    setOnboardingData: (state, payload) => {
      state.onboardingData = payload;
    },
    setIsPositionsCreated: (state, payload) => (state.isPositionsCreated = payload),
    setIsMembershipMannersCreated: (state, payload) => (state.isMembershipMannersCreated = payload),
    setIsZonesCreated: (state, payload) => (state.isZonesCreated = payload),
    setIsMinistriesCreated: (state, payload) => (state.isMinistriesCreated = payload),
    setIsServiceTypesCreated: (state, payload) => (state.isServiceTypesCreated = payload),
    setIsGivingTypesCreated: (state, payload) => (state.isGivingTypesCreated = payload),
    setIsHeadCountTypesCreated: (state, payload) => (state.isHeadCountTypesCreated = payload),
    setIsAbsenceReasonsCreated: (state, payload) => (state.isAbsenceReasonsCreated = payload),
    setIsServiceActivitiesCreated: (state, payload) => (state.isServiceActivitiesCreated = payload),
    setMigrationTemplatesSettings: (state, payload) => (state.migrationTemplatesSettings = payload),
    setDataMigrationProgress: (state, payload) => (state.dataMigrationProgress = payload),
    resetDataMigrationProgress: (state) => (state.dataMigrationProgress = defaultMigrationProgress),
  },
  actions: {
    async loadOnboardingState({ commit }, { churchId, stage }) {
      return $http('v2')
        .get(`/organisations/${churchId}/onboardings/${stage}`, { canBeImpersonated: true })
        .then((response) => {
          commit('setOnboardingState', response.data.data);
          return response;
        })
        .catch((error) => {
          commit('setOnboardingState', null);
          return Promise.reject(error);
        });
    },

    async loadMigrationTemplateSettings({ commit }, { churchId }) {
      return $http('v2')
        .get(`/organisations/${churchId}/onboardings/migration_templates_settings`, {
          canBeImpersonated: true,
        })
        .then((response) => {
          commit('setMigrationTemplatesSettings', response.data.data);
          response;
        })
        .catch((error) => {
          commit('setMigrationTemplatesSettings', null);
          return Promise.reject(error);
        });
    },

    async onboardingStageOneAction({ commit, getters }, { step, churchId }) {
      return $http('v2')
        .post(
          `/organisations/${churchId}/onboardings/onboarding_stage_one/${step}`,
          getters['getOnboardingData'],
          {
            notifiable: true,
          }
        )
        .then(() => {
          switch (step) {
            case 'create_giving_types':
              commit('setIsGivingTypesCreated', true);
              break;
            case 'create_positions':
              commit('setIsPositionsCreated', true);
              break;
            case 'create_membership_manners':
              commit('setIsMembershipMannersCreated', true);
              break;
            case 'create_zones':
              commit('setIsZonesCreated', true);
              break;
            case 'create_ministries':
              commit('setIsMinistriesCreated', true);
              break;
            case 'create_service_types':
              commit('setIsServiceTypesCreated', true);
              break;
            case 'create_service_activities':
              commit('setIsServiceActivitiesCreated', true);
              break;

            default:
              throw new Error('Invalid onboarding step');
          }
          return true;
        });
    },

    async migrateMembersAction(ctx, { batch, churchId }) {
      return $http('v2')
        .post(`/organisations/${churchId}/onboardings/members_migration`, batch, {
          notifiable: true,
        })
        .then((response) => {
          return response.data.data;
        });
    },
  },
};
